/* eslint-disable no-param-reassign -- gate entries and three.js objects are mutable records updated in place */
import * as THREE from 'three'

import type { Entry } from './types'

export interface VisibilityScratch {
  normalMatrix: THREE.Matrix3
  normal: THREE.Vector3
  worldPosition: THREE.Vector3
  toCamera: THREE.Vector3
  projScreenMatrix: THREE.Matrix4
  frustum: THREE.Frustum
  worldBounds: THREE.Box3
}

export const createVisibilityScratch = (): VisibilityScratch => ({
  normalMatrix: new THREE.Matrix3(),
  normal: new THREE.Vector3(),
  worldPosition: new THREE.Vector3(),
  toCamera: new THREE.Vector3(),
  projScreenMatrix: new THREE.Matrix4(),
  frustum: new THREE.Frustum(),
  worldBounds: new THREE.Box3(),
})

/** Refreshes the camera matrices and frustum used by `computeVisible`. */
export function updateFrustum(
  scratch: VisibilityScratch,
  camera: THREE.Camera
): void {
  camera.updateMatrixWorld()
  scratch.projScreenMatrix.multiplyMatrices(
    camera.projectionMatrix,
    camera.matrixWorldInverse
  )
  scratch.frustum.setFromProjectionMatrix(scratch.projScreenMatrix)
}

/**
 * True when the camera is in front of the mirror plane by more than the
 * facing threshold and the mirror's bounds intersect the frustum. Also
 * records the camera distance on the entry.
 */
export function computeVisible(
  scratch: VisibilityScratch,
  entry: Entry,
  camera: THREE.Camera
): boolean {
  const { reflector } = entry
  reflector.getWorldPosition(scratch.worldPosition)
  scratch.normalMatrix.getNormalMatrix(reflector.matrixWorld)
  scratch.normal.set(0, 0, 1).applyMatrix3(scratch.normalMatrix).normalize()
  scratch.toCamera.subVectors(camera.position, scratch.worldPosition)
  entry.distanceToCamera = scratch.toCamera.length()
  const frontDistance = scratch.toCamera.dot(scratch.normal)
  if (frontDistance <= 0 || frontDistance <= entry.getFacingThreshold()) {
    return false
  }
  scratch.worldBounds
    .copy(entry.localBounds)
    .applyMatrix4(reflector.matrixWorld)
  return scratch.frustum.intersectsBox(scratch.worldBounds)
}

export function snapshot(
  entry: Entry,
  camera: THREE.Camera,
  sceneVersion: number
): void {
  entry.lastCameraWorld.copy(camera.matrixWorld)
  entry.lastCameraProjection.copy(camera.projectionMatrix)
  entry.lastReflectorWorld.copy(entry.reflector.matrixWorld)
  entry.lastSceneVersion = sceneVersion
  entry.hasSnapshot = true
}

const isUnchanged = (
  entry: Entry,
  camera: THREE.Camera,
  sceneVersion: number
) =>
  entry.hasSnapshot &&
  entry.lastSceneVersion === sceneVersion &&
  entry.lastCameraWorld.equals(camera.matrixWorld) &&
  entry.lastCameraProjection.equals(camera.projectionMatrix) &&
  entry.lastReflectorWorld.equals(entry.reflector.matrixWorld)

export function needsRender(
  entry: Entry,
  camera: THREE.Camera,
  sceneVersion: number
): boolean {
  if (entry.getUpdateInterval() <= 0) return !entry.renderedOnce
  return !isUnchanged(entry, camera, sceneVersion)
}
